import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Loader2,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  RefreshCw,
  Database,
  Zap,
  Mail,
  CreditCard,
  Shield,
  Activity,
  Info,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { useSeason } from '@/context/SeasonContext'
import { supabase } from '@/integrations/supabase/client'

interface SyncLog {
  id: string
  sync_type: string
  season: string
  matchdays_synced: number[]
  fixtures_updated: number
  results_updated: number
  selections_processed: number
  errors: string[] | null
  duration_ms: number
  created_at: string
}

interface AuditLogSummary {
  action: string
  count: number
  latest: string
  has_errors: boolean
}

interface FunctionStatus {
  name: string
  description: string
  fullDescription: string
  icon: React.ReactNode
  status: 'healthy' | 'warning' | 'error' | 'unknown'
  lastRun: string | null
  lastRunRelative: string
  details: string
  errorCount: number
}

function getRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function StatusBadge({ status }: { status: FunctionStatus['status'] }) {
  switch (status) {
    case 'healthy':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3" />
          Healthy
        </span>
      )
    case 'warning':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          Warning
        </span>
      )
    case 'error':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="h-3 w-3" />
          Error
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
          <Clock className="h-3 w-3" />
          No data
        </span>
      )
  }
}

export default function AdminOperations() {
  const { isSuperAdmin } = useAuth()
  const { season } = useSeason()
  const [infoFunction, setInfoFunction] = useState<FunctionStatus | null>(null)

  // Fetch recent sync logs (last 50)
  const { data: syncLogs = [], isLoading: isLoadingSyncs } = useQuery({
    queryKey: ['admin-ops-sync-logs', season],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sync_logs')
        .select('*')
        .eq('season', season)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data || []) as SyncLog[]
    },
    enabled: isSuperAdmin,
    refetchInterval: 60000, // Auto-refresh every minute
  })

  // Fetch audit log action counts (last 7 days)
  const { data: auditSummary = [], isLoading: isLoadingAudit } = useQuery({
    queryKey: ['admin-ops-audit-summary'],
    queryFn: async () => {
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

      const { data, error } = await supabase
        .from('audit_logs')
        .select('action, created_at')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(500)

      if (error) throw error

      // Aggregate by action
      const actionMap: Record<string, { count: number; latest: string }> = {}
      for (const row of data || []) {
        if (!actionMap[row.action]) {
          actionMap[row.action] = { count: 0, latest: row.created_at }
        }
        actionMap[row.action].count++
        if (row.created_at > actionMap[row.action].latest) {
          actionMap[row.action].latest = row.created_at
        }
      }

      return Object.entries(actionMap).map(([action, { count, latest }]) => ({
        action,
        count,
        latest,
        has_errors: false,
      })) as AuditLogSummary[]
    },
    enabled: isSuperAdmin,
  })

  // Derive function statuses from sync logs and audit logs
  const functionStatuses: FunctionStatus[] = useMemo(() => {
    const now = new Date()
    const oneHourAgo = new Date(now.getTime() - 3600000)
    const sixHoursAgo = new Date(now.getTime() - 6 * 3600000)
    const oneDayAgo = new Date(now.getTime() - 86400000)

    // Helper to find latest sync log by type
    const latestByType = (type: string) => syncLogs.find(l => l.sync_type === type)
    const logsByType = (type: string) => syncLogs.filter(l => l.sync_type === type)
    const recentErrorsByType = (type: string) =>
      logsByType(type).filter(l => l.errors && l.errors.length > 0 && new Date(l.created_at) > oneDayAgo)

    // Helper to find latest audit log by action prefix
    const latestAudit = (actionPrefix: string) =>
      auditSummary.find(a => a.action.startsWith(actionPrefix))

    // cron-sync-fixtures status
    const latestCron = latestByType('cron-sync') || latestByType('fixtures')
    const cronErrors = recentErrorsByType('cron-sync').length + recentErrorsByType('fixtures').length
    const cronStatus: FunctionStatus['status'] = !latestCron
      ? 'unknown'
      : cronErrors > 3
        ? 'error'
        : cronErrors > 0
          ? 'warning'
          : latestCron && new Date(latestCron.created_at) < sixHoursAgo
            ? 'warning'
            : 'healthy'

    // Results processing status
    const latestResults = latestByType('results') || latestByType('populate-results')
    const resultsErrors = recentErrorsByType('results').length
    const resultsStatus: FunctionStatus['status'] = !latestResults
      ? 'unknown'
      : resultsErrors > 0
        ? 'warning'
        : 'healthy'

    // Selections processing status
    const latestSelections = latestByType('selections') || latestByType('process-selections')
    const selectionsErrors = recentErrorsByType('selections').length
    const selectionsStatus: FunctionStatus['status'] = !latestSelections
      ? 'unknown'
      : selectionsErrors > 0
        ? 'warning'
        : 'healthy'

    // Autopicks
    const autopickAudit = latestAudit('autopick')
    const autopickStatus: FunctionStatus['status'] = autopickAudit ? 'healthy' : 'unknown'

    // Deadline reminders
    const reminderAudit = latestAudit('reminder') || latestAudit('deadline')
    const reminderStatus: FunctionStatus['status'] = reminderAudit ? 'healthy' : 'unknown'

    // Payments (Stripe)
    const paymentAudit = latestAudit('payment')
    const paymentStatus: FunctionStatus['status'] = paymentAudit ? 'healthy' : 'unknown'

    return [
      {
        name: 'Fixture Sync (Cron)',
        description: 'Automated fixture/score sync every 15 min during match hours',
        fullDescription: 'The cron-sync-fixtures function is the backbone of the data pipeline. It runs automatically every 15 minutes during EPL match windows (typically Sat/Sun afternoons and midweek evenings).\n\nWhat it does:\n• Fetches the latest fixture data from API-Football for upcoming and in-progress matchdays\n• Updates kickoff times, scores, and match statuses (NS → 1H → HT → 2H → FT)\n• Detects newly completed matches and triggers results processing\n• Schedules autopick jobs and deadline reminder emails via QStash when new fixtures with future deadlines are found\n• Sends "picks are open" notifications when a new matchday becomes available\n\nTriggered by: Supabase cron schedule (pg_cron) or manually from the Admin page.',
        icon: <RefreshCw className="h-5 w-5" />,
        status: cronStatus,
        lastRun: latestCron?.created_at || null,
        lastRunRelative: latestCron ? getRelativeTime(latestCron.created_at) : 'Never',
        details: latestCron
          ? `${latestCron.fixtures_updated} fixtures, ${latestCron.results_updated} results`
          : 'No sync logs found',
        errorCount: cronErrors,
      },
      {
        name: 'Results Processing',
        description: 'Populates match results with red cards from API',
        fullDescription: 'The populate-match-results function enriches completed fixtures with detailed match data needed for scoring.\n\nWhat it does:\n• Fetches match events (goals, red cards) from API-Football for finished matches\n• Updates each fixture row with home/away scores, red card counts, and match statistics\n• Marks fixtures as results-populated so they can be scored\n• This is Step 1 of the two-step results pipeline — it must run before selection scoring\n\nTriggered by: The "Populate Results" button on the Admin page, or automatically after fixture sync detects completed matches.',
        icon: <Database className="h-5 w-5" />,
        status: resultsStatus,
        lastRun: latestResults?.created_at || null,
        lastRunRelative: latestResults ? getRelativeTime(latestResults.created_at) : 'Never',
        details: latestResults
          ? `${latestResults.results_updated} results updated`
          : 'No results processing logs',
        errorCount: resultsErrors,
      },
      {
        name: 'Selection Scoring',
        description: 'Calculates points for user picks based on match results',
        fullDescription: 'The process-matchday-selections function is Step 2 of the results pipeline. It scores every manager\'s pick for a given matchday.\n\nWhat it does:\n• Reads each user\'s team selection for the matchday\n• Looks up the actual match result for that team\n• Calculates points: Win = 3pts, Draw = 1pt, Loss = 0pts, Red card bonus = +1pt per red card on the opponent\n• Writes the score and result breakdown to the user_selections table\n• Updates the standings/leaderboard accordingly\n\nTriggered by: The "Process Selections" button on the Admin page, after results have been populated.',
        icon: <Zap className="h-5 w-5" />,
        status: selectionsStatus,
        lastRun: latestSelections?.created_at || null,
        lastRunRelative: latestSelections ? getRelativeTime(latestSelections.created_at) : 'Never',
        details: latestSelections
          ? `${latestSelections.selections_processed || 0} selections scored`
          : 'No scoring logs',
        errorCount: selectionsErrors,
      },
      {
        name: 'Autopicks',
        description: 'Assigns picks to users who missed the deadline',
        fullDescription: 'The process-autopicks function ensures every manager has a pick for each matchday, even if they forgot to submit one.\n\nWhat it does:\n• Runs automatically 30 seconds after the matchday deadline passes\n• Finds all active league members who haven\'t submitted a pick\n• Assigns them the lowest-ranked available team they haven\'t already used this season\n• Marks the pick as an "autopick" so it\'s visually distinguished in the UI\n• Logs each autopick assignment for audit purposes\n\nTriggered by: QStash scheduled job (set up automatically when fixtures are synced with a future deadline).',
        icon: <Shield className="h-5 w-5" />,
        status: autopickStatus,
        lastRun: autopickAudit?.latest || null,
        lastRunRelative: autopickAudit ? getRelativeTime(autopickAudit.latest) : 'Never',
        details: autopickAudit
          ? `${autopickAudit.count} events (7 days)`
          : 'No autopick events found',
        errorCount: 0,
      },
      {
        name: 'Deadline Reminders',
        description: 'Sends email reminders 3 days, 24h, and 6h before pick deadline',
        fullDescription: 'The send-deadline-reminders function sends personalized email reminders to all league members before a matchday deadline.\n\nWhat it does:\n• Sends three reminders per matchday: 3 days before, 24 hours before, and 6 hours before the deadline\n• Personalizes each email — users who already picked get a "review your pick" message, users who haven\'t get an "make your pick" nudge\n• Uses Resend for email delivery with rate limiting (1 email/second)\n• Idempotent — checks sync_logs to prevent duplicate sends\n• Supports DEV_EMAIL mode for testing without emailing real users\n\nTriggered by: QStash scheduled jobs (set up automatically during fixture sync) or manually by a super admin.',
        icon: <Mail className="h-5 w-5" />,
        status: reminderStatus,
        lastRun: reminderAudit?.latest || null,
        lastRunRelative: reminderAudit ? getRelativeTime(reminderAudit.latest) : 'Never',
        details: reminderAudit
          ? `${reminderAudit.count} emails (7 days)`
          : 'No reminder events found',
        errorCount: 0,
      },
      {
        name: 'Payments (Stripe)',
        description: 'Processes league entry fee payments via Stripe',
        fullDescription: 'The Stripe payment integration handles league entry fee collection.\n\nWhat it does:\n• Creates Stripe Checkout sessions when a member clicks "Pay" on the billing page\n• Processes webhook callbacks from Stripe when payments complete\n• Updates the member\'s payment_status (pending → paid) and records the amount and timestamp\n• Supports admin-managed manual status updates (paid, partial, waived)\n\nTriggered by: User clicking the pay button (checkout session) or Stripe webhook (payment confirmation). Currently in development — coming soon.',
        icon: <CreditCard className="h-5 w-5" />,
        status: paymentStatus,
        lastRun: paymentAudit?.latest || null,
        lastRunRelative: paymentAudit ? getRelativeTime(paymentAudit.latest) : 'Never',
        details: paymentAudit
          ? `${paymentAudit.count} events (7 days)`
          : 'No payment events',
        errorCount: 0,
      },
    ]
  }, [syncLogs, auditSummary])

  // Recent sync log history (last 10)
  const recentLogs = syncLogs.slice(0, 10)

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="text-center py-16 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">Access Denied</p>
        </div>
      </AppShell>
    )
  }

  const isLoading = isLoadingSyncs || isLoadingAudit

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Operations Dashboard</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              System health and function status · {season}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : (
          <>
            {/* Function Status Cards */}
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                Function Status
              </h2>
              <div className="space-y-2">
                {functionStatuses.map((fn) => (
                  <div
                    key={fn.name}
                    className="flex items-start gap-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                  >
                    <div className="text-zinc-400 mt-0.5 shrink-0">
                      {fn.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {fn.name}
                        </span>
                        <StatusBadge status={fn.status} />
                      </div>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                        {fn.description}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-400 dark:text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {fn.lastRunRelative}
                        </span>
                        <span>{fn.details}</span>
                        {fn.errorCount > 0 && (
                          <span className="text-amber-600 dark:text-amber-400">
                            {fn.errorCount} error{fn.errorCount !== 1 ? 's' : ''} (24h)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setInfoFunction(fn)}
                      className="p-1 rounded-md text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors shrink-0 mt-0.5"
                      aria-label={`Info about ${fn.name}`}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* Recent Sync History */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Recent Sync History
                </h2>
                <Link
                  to="/admin/logs"
                  className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                >
                  View all
                </Link>
              </div>

              {recentLogs.length === 0 ? (
                <p className="text-sm text-zinc-400 dark:text-zinc-500 text-center py-6">
                  No sync logs found for {season}
                </p>
              ) : (
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
                  {recentLogs.map((log) => {
                    const hasErrors = log.errors && log.errors.length > 0
                    return (
                      <div
                        key={log.id}
                        className="flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-zinc-900"
                      >
                        {hasErrors ? (
                          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300 capitalize">
                              {log.sync_type.replace(/[-_]/g, ' ')}
                            </span>
                            {log.matchdays_synced?.length > 0 && (
                              <span className="text-xs text-zinc-400">
                                MD {log.matchdays_synced.join(', ')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-zinc-400 dark:text-zinc-500">
                            <span>{log.fixtures_updated}f</span>
                            <span>{log.results_updated}r</span>
                            <span>{log.selections_processed || 0}s</span>
                            <span>{log.duration_ms}ms</span>
                          </div>
                        </div>
                        <span className="text-xs text-zinc-400 dark:text-zinc-500 shrink-0">
                          {getRelativeTime(log.created_at)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            {/* Audit Activity Summary */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Activity Summary (7 days)
                </h2>
                <Link
                  to="/admin/activity"
                  className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                >
                  View all
                </Link>
              </div>

              {auditSummary.length === 0 ? (
                <p className="text-sm text-zinc-400 dark:text-zinc-500 text-center py-6">
                  No activity in the last 7 days
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {auditSummary
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                    .map((item) => (
                      <div
                        key={item.action}
                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                      >
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300 truncate block">
                            {item.action.replace(/\./g, ' · ')}
                          </span>
                          <span className="text-xs text-zinc-400">{getRelativeTime(item.latest)}</span>
                        </div>
                        <span className="text-sm font-mono font-medium text-zinc-500 dark:text-zinc-400 ml-2">
                          {item.count}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Function Info Dialog */}
      <Dialog open={!!infoFunction} onOpenChange={(open) => !open && setInfoFunction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {infoFunction?.icon}
              {infoFunction?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-600 dark:text-zinc-400">
            {infoFunction?.fullDescription.split('\n\n').map((paragraph, i) => {
              if (paragraph.startsWith('What it does:') || paragraph.startsWith('Triggered by:')) {
                const [heading, ...rest] = paragraph.split('\n')
                return (
                  <div key={i}>
                    <p className="font-medium text-zinc-900 dark:text-zinc-100 mb-1">{heading}</p>
                    {rest.map((line, j) => (
                      <p key={j} className="ml-1">{line}</p>
                    ))}
                  </div>
                )
              }
              return <p key={i}>{paragraph}</p>
            })}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  )
}
